import {useEffect, useState} from "react";
import {
    Alert, Button, Card, Empty, Flex, Form, Input, Modal,
    Popconfirm, Table, Tag, Typography, message
} from "antd";
import {KeyOutlined, PlusOutlined, StopOutlined} from '@ant-design/icons';
import Apiurl from "../ConstURL";

const {Title, Text, Paragraph} = Typography;

// Go отдаёт «пустое» время как 0001-01-01 — это значит «ещё не было»
function formatDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) return "—";
    return date.toLocaleString("ru-RU");
}

const examples = [
    {
        title: "1. Создать задачу — массив SKU в JSON (в ответе поле Taskid вида TASK:…)",
        code: `curl -X POST "${Apiurl}/task/create" \\
  -H "Authorization: Bearer zp_ВАШ_ТОКЕН" \\
  -H "Content-Type: application/json" \\
  -d '["GRG4628705", "G6006393"]'`,
    },
    {
        title: "2. Узнать статус (wait → running → done)",
        code: `curl "${Apiurl}/task/get/info/<Taskid>?token=zp_ВАШ_ТОКЕН"`,
    },
    {
        title: "3. Забрать результат, когда статус done",
        code: `curl "${Apiurl}/task/get/<Taskid>?token=zp_ВАШ_ТОКЕН"`,
    },
];

export default function Apitokens() {
    const [form] = Form.useForm();
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createdToken, setCreatedToken] = useState(null);

    function fetchTokens() {
        setLoading(true);
        fetch(Apiurl + "/apitokens/list?" + new URLSearchParams({
            token: localStorage.getItem("token"),
        }))
            .then(response => response.json())
            .then(data => {
                if (data.Status) {
                    setTokens(data.Data || []);
                } else {
                    message.error("Не удалось загрузить токены");
                }
            })
            .catch(err => message.error("Ошибка при загрузке: " + err.message))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchTokens();
    }, []);

    const createToken = (values) => {
        setCreating(true);
        fetch(Apiurl + "/apitokens/create?" + new URLSearchParams({
            token: localStorage.getItem("token"),
        }), {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({name: values.name}),
        })
            .then(response => response.json())
            .then(data => {
                if (data.Status) {
                    setCreatedToken({token: data.Token, name: data.Data.Name});
                    form.resetFields();
                    fetchTokens();
                } else {
                    message.error("Токен не создан: " + data.Message);
                }
            })
            .catch(err => message.error("Ошибка при создании: " + err.message))
            .finally(() => setCreating(false));
    };

    const revokeToken = (id) => {
        fetch(Apiurl + "/apitokens/revoke/" + id + "?" + new URLSearchParams({
            token: localStorage.getItem("token"),
        }), {method: "POST"})
            .then(response => response.json())
            .then(data => {
                if (data.Status) {
                    message.success("Токен отозван");
                    fetchTokens();
                } else {
                    message.error("Токен не отозван");
                }
            })
            .catch(err => message.error("Ошибка при отзыве: " + err.message));
    };

    const columns = [
        {
            title: "Название",
            dataIndex: "Name",
            key: "name",
            render: (name) => <Text strong>{name}</Text>,
        },
        {
            title: "Токен",
            dataIndex: "Preview",
            key: "preview",
            render: (preview) => <Text code>{preview}</Text>,
        },
        {
            title: "Создал",
            dataIndex: "Username",
            key: "username",
        },
        {
            title: "Создан",
            dataIndex: "CreatedAt",
            key: "created",
            render: formatDate,
        },
        {
            title: "Последнее использование",
            dataIndex: "LastUsedAt",
            key: "lastused",
            render: formatDate,
        },
        {
            title: "Статус",
            key: "status",
            render: (_, record) => record.Revoked
                ? <Tag color="error">Отозван</Tag>
                : <Tag color="success">Активен</Tag>,
        },
        {
            title: "",
            key: "actions",
            align: "right",
            render: (_, record) => !record.Revoked && (
                <Popconfirm
                    title="Отозвать токен?"
                    description="Все интеграции с этим токеном сразу перестанут работать"
                    onConfirm={() => revokeToken(record.Id)}
                    okText="Отозвать"
                    cancelText="Отмена"
                    okButtonProps={{danger: true}}
                >
                    <Button danger type="text" icon={<StopOutlined/>}>Отозвать</Button>
                </Popconfirm>
            ),
        },
    ];

    const activeCount = tokens.filter(token => !token.Revoked).length;

    return (
        <div>
            <Flex justify="space-between" align="center" style={{marginBottom: 24}}>
                <Title level={3} style={{margin: 0}}>API-токены</Title>
                <Text type="secondary">{activeCount} активных</Text>
            </Flex>

            <Card style={{maxWidth: 760, marginBottom: 16}}>
                <Paragraph type="secondary">
                    Постоянный токен для парсинга через API: не истекает и работает, пока его не отозвать.
                    Даёт доступ только к созданию задач, их статусу, результату и отмене —
                    и только к задачам, созданным от вашего имени.
                </Paragraph>
                <Form form={form} layout="inline" onFinish={createToken} autoComplete="off">
                    <Form.Item
                        name="name"
                        rules={[{required: true, whitespace: true, message: "Введите название токена"}]}
                        style={{flex: 1, minWidth: 240}}
                    >
                        <Input
                            prefix={<KeyOutlined style={{color: 'rgba(255,255,255,0.3)'}}/>}
                            placeholder="Название, например «Интеграция с магазином»"
                            maxLength={100}
                        />
                    </Form.Item>
                    <Form.Item style={{marginInlineEnd: 0}}>
                        <Button type="primary" htmlType="submit" loading={creating} icon={<PlusOutlined/>}>
                            Создать токен
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            <Card style={{marginBottom: 16}}>
                <Table
                    rowKey="Id"
                    columns={columns}
                    dataSource={tokens}
                    loading={loading}
                    pagination={false}
                    scroll={{x: true}}
                    locale={{emptyText: <Empty description="Токенов пока нет"/>}}
                />
            </Card>

            <Card title="Как пользоваться" size="small" style={{maxWidth: 760}}>
                <Paragraph type="secondary">
                    Токен передаётся в заголовке <Text code>Authorization: Bearer</Text> или
                    параметром <Text code>?token=</Text>. Полное описание API — в README.md бэкенда.
                </Paragraph>
                {examples.map((example) => (
                    <div key={example.title} style={{marginBottom: 12}}>
                        <Text strong>{example.title}</Text>
                        <Paragraph copyable={{text: example.code}} style={{marginBottom: 0}}>
                            <pre className="code-block">{example.code}</pre>
                        </Paragraph>
                    </div>
                ))}
            </Card>

            <Modal
                open={createdToken !== null}
                title={"Токен «" + (createdToken ? createdToken.name : "") + "» создан"}
                onCancel={() => setCreatedToken(null)}
                maskClosable={false}
                footer={
                    <Button type="primary" onClick={() => setCreatedToken(null)}>
                        Я сохранил токен
                    </Button>
                }
            >
                <Alert
                    type="warning"
                    showIcon
                    message="Скопируйте токен сейчас"
                    description="Он показывается один раз: в базе хранится только его хэш, восстановить токен нельзя — только выпустить новый."
                    style={{marginBottom: 16}}
                />
                {createdToken && (
                    <Paragraph copyable={{text: createdToken.token}} style={{marginBottom: 0}}>
                        <Text code style={{wordBreak: "break-all"}}>{createdToken.token}</Text>
                    </Paragraph>
                )}
            </Modal>
        </div>
    );
}
